import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/buttons/Button';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import InfoBanner from '../components/InfoBanner/InfoBanner';
import { useAuth } from '../context/AuthContext';
import { authManager } from '../services/AuthManager';
import { enterpriseService } from '../services';
import Input from '../components/inputs/Input';
import {
  FaCrown,
  FaSync,
  FaSignOutAlt,
  FaRocket,
  FaStar,
  FaChartLine,
  FaMicrophone,
  FaFileAlt,
  FaClock,
  FaLock,
  FaCheckCircle,
  FaArrowRight,
  FaMagic,
  FaStethoscope,
  FaBuilding
} from 'react-icons/fa';
import './SubscriptionRequired.css';

function SubscriptionRequired() {
  const { refreshProfile, profile } = useAuth();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const { isExpired, formattedEndDate, hasEndDate } = useMemo(() => {
    const endDate = profile?.subscription?.endDate || profile?.subscription?.end_date;
    if (!endDate) return { isExpired: false, formattedEndDate: null, hasEndDate: false };
    const d = new Date(endDate);
    const expired = !isNaN(d.getTime()) && d.getTime() < Date.now();
    const formatted = expired ? d.toLocaleDateString('fr-CH', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
    return { isExpired: expired, formattedEndDate: formatted, hasEndDate: true };
  }, [profile]);

  // If subscription is active, redirect to home
  useEffect(() => {
    const active = profile?.isSubscriptionActive === true || profile?.subscription?.status === 'active';
    if (active) {
      navigate('/home', { replace: true });
    }
  }, [profile, navigate]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshProfile();
    } catch (_) {
      // ignore
    } finally {
      // Force a full reload to re-evaluate routing guards
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    try {
      await authManager.logout();
      // After full cleanup, return to landing
      navigate('/', { replace: true });
    } catch (_) {
      // Even if logout fails, ensure navigation to landing
      navigate('/', { replace: true });
    }
  };

  const handleToggleInvite = (e) => {
    try { if (e && e.preventDefault) e.preventDefault(); } catch (_) {}
    setShowInviteForm(prev => !prev);
  };

  const handleInviteSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!inviteCode.trim()) {
      setInviteError("Veuillez entrer un code d'organisation");
      return;
    }
    setInviteSubmitting(true);
    setInviteError('');
    try {
      await enterpriseService.redeemEnterpriseInvite(inviteCode.trim());
      setInviteSuccess(true);
      // Refresh profile and force reload to ensure subscription status is properly updated
      try {
        await refreshProfile();
      } catch (_) {}
      // Force a full reload to re-evaluate routing guards (like handleRefresh does)
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Code invalide ou expiré';
      setInviteError(msg);
    } finally {
      setInviteSubmitting(false);
    }
  };

  

  const benefits = useMemo(() => {
    if (!hasEndDate) {
      // First account: no historical content; focus on value and quick start
      return [
        { icon: FaStar, title: "30 jours d'essai gratuit", description: 'Découvrez voxia sans engagement' },
        { icon: FaStethoscope, title: 'Par des médecins, pour des médecins', description: 'Développé pour faciliter la vie des médecins' },
      ];
    }
    // Returning user without active sub
    return [
      { icon: FaFileAlt, title: 'Retrouvez vos rapports', description: 'Accès à tous vos documents passés' },
      { icon: FaMagic, title: 'Récupérez vos modèles entraînés', description: 'IA déjà entraînée à votre pratique' },
    ];
  }, [hasEndDate]);

  return (
    <div className="subscription-required-container">
      <div className="subscription-hero-gradient" />
      
      <Card className="subscription-required-card card-premium">

        <SectionHeader 
          icon={FaRocket} 
          title={isExpired ? 'Réactivez votre accès voxia' : 'Débloquez tout le potentiel de voxia'} 
        />

        <div className="subscription-status-section">
          {isExpired && (
            <InfoBanner
              icon={FaCrown}
              title="Abonnement expiré"
              className="subscription-alert-banner"
            >
              Votre abonnement est expiré{formattedEndDate ? ` depuis le ${formattedEndDate}` : ''}.
              Renouvelez-le maintenant pour retrouver l'accès complet à voxia.
            </InfoBanner>
          )}
        </div>

        <div className="subscription-cta-section">
          <Link to="/subscription/plans" className="subscription-cta-button pulse-button">
            <FaCrown className="cta-icon" />
            {hasEndDate ? 'Choisir mon abonnement' : 'Commencer mon essai gratuit'}
          </Link>

          <div style={{ marginTop: '14px', textAlign: 'center' }}>
            <a href="#invite" onClick={handleToggleInvite} className="enterprise-invite-link">
              <FaBuilding style={{ marginRight: '6px' }} />
              Rejoindre une organisation
            </a>
          </div>
        </div>

        {showInviteForm && (
          <div className="enterprise-invite-inline" style={{ marginTop: 16 }}>
            <InfoBanner>
              Entrez le code d'invitation fourni par votre organisation.
            </InfoBanner>
            <form onSubmit={handleInviteSubmit} className="enterprise-invite-form" style={{ marginTop: 12 }}>
              <Input
                id="enterprise-code-inline"
                type="text"
                label="Code d'organisation"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                disabled={inviteSubmitting || inviteSuccess}
              />
              {inviteError && (
                <div className="error-message" style={{ marginTop: 8 }}>
                  {inviteError}
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <Button
                  htmlType="submit"
                  variant="primary"
                  icon={inviteSubmitting ? FaSync : FaCheckCircle}
                  isSpinning={inviteSubmitting}
                  disabled={!inviteCode.trim() || inviteSubmitting || inviteSuccess}
                >
                  {inviteSubmitting ? 'Vérification...' : "Rejoindre l'organisation"}
                </Button>
              </div>
              {inviteSuccess && (
                <div style={{ marginTop: 10, color: 'var(--color-success)' }}>
                  Code accepté. Redirection…
                </div>
              )}
            </form>
          </div>
        )}

        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card animate-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="benefit-icon-wrapper">
                <benefit.icon className="benefit-icon" />
              </div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="subscription-secondary-actions">
          <Button 
            variant="secondary" 
            onClick={handleRefresh}
            icon={FaSync}
            disabled={isRefreshing}

          >
            {isRefreshing ? 'Vérification...' : 'Actualiser mon statut'}
          </Button>

          <Button 
            variant="danger" 
            onClick={handleLogout}
            icon={FaSignOutAlt}
            
          >
            Se déconnecter
          </Button>
        </div>

        <div style={{
          textAlign: 'center',
          color: '#999',
          fontSize: '12px',
          marginTop: '20px',
          padding: '0 16px',
          lineHeight: '1.6'
        }}>
          <div>
            En continuant, vous acceptez nos{' '}
            <span
              onClick={() => navigate('/terms')}
              style={{
                color: '#999',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              conditions générales de vente
            </span>
          </div>
        </div>

      </Card>
    </div>
  );
}

export default SubscriptionRequired;